import moment from 'moment-jalaali';
import { memo, FC, useState } from 'react';

import APopover from 'components/APopover';
import BubbleLoading from 'components/BubbleLoading';
import SanitizedHTML from 'react-sanitized-html';
import { iconSelector } from 'utils/fileUtils';
import { convertDigitToProperFormat } from 'utils/number';
import { Centered, MessageFooter } from 'utils/style/extendable';
import { blue, red } from '../../../utils/style/colors';
import {
  AvatarWrapper,
  ChatMessage,
  ChatMessageWrapper,
  ImageMessage,
  LoadingParent,
  MessageTime,
  MessageWrapper,
  SenderName,
  SystemMessage,
  UserAvatar,
} from './styles';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { getLanguageBaseOnTZ } from '../../../utils/LanguageLocalStorage';
import { smartDateString } from '../../../utils/date';
import { useTranslation } from 'react-i18next';

interface IComponentProps {
  content: any; // TODO
  uploading: boolean;
  senderName: string;
  messageTime: number;
  whoIsSender: 'others' | 'myself' | 'system';
  thisUser: IThisUserState;
}

const MAX_SYSTEM_LENGTH = 300;

const MessageView: FC<IComponentProps> = ({
  content,
  uploading,
  senderName,
  messageTime,
  whoIsSender,
  thisUser,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const lang = getLanguageBaseOnTZ();
  const { t } = useTranslation('taskManager');
  const markupedContent = content.text
    ?.toString()
    .replaceAll(
      RegExp(`@\\[U${thisUser.userId}]\\(([^)]+)\\)`, 'g'),
      `<b style="color:${red}">@$1</b>`
    )
    .replaceAll(/@\[U[0-9]+]\(([^)]+)\)/g, `<b style="color:${blue}">@$1</b>`);

  const getSystemMessageContent = () => {
    const fullContent = content.messages
      .map((item: any) => item.value)
      .join(' ');

    const shouldBeingCollappsed = fullContent.length > MAX_SYSTEM_LENGTH;

    if (shouldBeingCollappsed && !isExpanded) {
      return (
        fullContent.slice(0, MAX_SYSTEM_LENGTH) +
        '...' +
        `<b>${t('readMore')}</b>`
      );
    }

    return fullContent;
  };

  return (
    <ChatMessageWrapper whoIsSender={whoIsSender}>
      {whoIsSender === 'others' && (
        <AvatarWrapper>
          <MessageTime>{smartDateString(messageTime, true)}</MessageTime>
          <SenderName>{senderName}</SenderName>
          <UserAvatar />
        </AvatarWrapper>
      )}
      <MessageWrapper>
        {content.type === 'file' || content.type === 'simple' ? (
          <APopover
            arrow
            curveBorder
            disabled={whoIsSender !== 'myself'}
            trigger="hover"
            target={
              <ChatMessage
                hasImage={uploading}
                selfMessage={whoIsSender === 'myself'}
              >
                {content.url && (
                  <a
                    href={content.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'flex', borderRadius: '25px' }}
                  >
                    {content.thumbnailUrl ? (
                      <ImageMessage
                        src={content.thumbnailUrl}
                        alt={content.text}
                      />
                    ) : (
                      <ImageMessage
                        src={iconSelector(content.fileType)}
                        alt={content.text}
                      />
                    )}
                  </a>
                )}
                {
                  <SanitizedHTML
                    allowedAttributes={{ b: ['style'] }}
                    allowedTags={['b']}
                    html={markupedContent}
                  />
                }
                {uploading && (
                  <LoadingParent>
                    <BubbleLoading />
                  </LoadingParent>
                )}
              </ChatMessage>
            }
          >
            <Centered>
              {lang === 'fa'
                ? (convertDigitToProperFormat(
                    moment(messageTime).format('jD jMMMM HH:mm')
                  ) as string)
                : convertDigitToProperFormat(
                    moment(messageTime).locale('en').format('MMMM D HH:mm')
                  )}
            </Centered>
          </APopover>
        ) : (
          <SystemMessage
            isCollapsible={
              content.messages.map((item: any) => item.value).join(' ').length >
              MAX_SYSTEM_LENGTH
            }
            onClick={() => {
              const fullContent = content.messages
                .map((item: any) => item.value)
                .join(' ');
              if (
                whoIsSender === 'system' &&
                fullContent.length > MAX_SYSTEM_LENGTH
              ) {
                setIsExpanded(!isExpanded);
              }
            }}
          >
            <div className="flex flex-col gap-2">
              <SanitizedHTML html={getSystemMessageContent()} />
              <MessageFooter>
                <div className="flex">
                  <p>
                    {getLanguageBaseOnTZ() === 'fa'
                      ? (convertDigitToProperFormat(
                          moment(messageTime).format('jD jMMMM HH:mm')
                        ) as string)
                      : convertDigitToProperFormat(
                          moment(messageTime)
                            .locale('en')
                            .format('MMMM D HH:mm')
                        )}
                  </p>
                </div>
              </MessageFooter>
            </div>
          </SystemMessage>
        )}
      </MessageWrapper>
    </ChatMessageWrapper>
  );
};

export default memo(MessageView);
